import { useQueryClient } from "@tanstack/react-query";
import { Trash2Icon } from "lucide-react";
import { create } from "zustand";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { toast } from "@/components/toast/toast-queue";

import { useMoveFilesToTrashMutation } from "../hooks/useMoveFilesToTrashMutation";

type DeleteFilesDialogState = {
  isOpen: boolean;
  selectedItemsIds: number[];
  setSelectedItemsIds: (selectedItemsIds: number[]) => void;
  toggle: () => void;
};
export const useDeleteFilesDialogState = create<DeleteFilesDialogState>()(
  (set) => ({
    isOpen: false,
    selectedItemsIds: [],
    setSelectedItemsIds: (selectedItemsIds: number[]) =>
      set(() => ({ selectedItemsIds })),
    toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  }),
);

export function FileDeleteDialog({
  resetRowSelection,
}: {
  resetRowSelection: () => void;
}) {
  const { isOpen, selectedItemsIds, toggle } = useDeleteFilesDialogState();
  const queryClient = useQueryClient();

  const moveToTrashMutation = useMoveFilesToTrashMutation({
    onError: () => {
      toast.add({
        description: "Cannot delete files with feedback",
        title: "Error deleting message",
        type: "error",
      });
    },
    onSuccess: () => {
      resetRowSelection();
      toggle();
      queryClient.invalidateQueries({ queryKey: ["myFilesStructure"] });
    },
  });

  return (
    <Modal
      classNames={{ modal: "w-[400px]" }}
      isDismissable={false}
      isOpen={isOpen}
    >
      <Dialog alert>
        <DialogHeader className="flex flex-col gap-[16px]">
          <div className="size-12 bg-bg-error-secondary rounded-full grid place-items-center text-featured-icon-light-fg-error">
            <Trash2Icon size={20} />
          </div>
          <span>Delete asset</span>
        </DialogHeader>
        <DialogCloseButton onPress={toggle} />
        <DialogBody className="pt-[4px]">
          <span className="text-sm text-text-tertiary">
            Are you sure you want to delete this asset? This asset wil be moved
            to the trash folder.
          </span>
        </DialogBody>
        <DialogFooter className="flex gap-lg">
          <Button
            className="flex-1"
            color="gray"
            onPress={toggle}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            color="destructive"
            isDisabled={moveToTrashMutation.isPending}
            isLoading={moveToTrashMutation.isPending}
            onPress={() => {
              // Need to filter out folders
              const filteredSelectedItemsIds = selectedItemsIds.filter(
                (id) => typeof id === "number",
              );

              moveToTrashMutation.mutate(filteredSelectedItemsIds as number[]);
            }}
          >
            {moveToTrashMutation.isPending ? <></> : <span>Delete</span>}
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
